<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import MdiPlayBoxOutline from '~icons/mdi/play-box-outline'
import MdiChevronDown from '~icons/mdi/chevron-down'
import RunMacroModal from './RunMacroModal.vue'
import { getMacros } from '@/api/api.js'

const props = defineProps({
  // Matches Macro.model_type: 'project' | 'bundle' | 'item_metadata'
  modelType: { type: String, required: true },
  // ID of the current page's object — will be pre-filled in the matching input
  modelId: { type: [Number, String], required: true },
})

const macros = ref([])
const loading = ref(true)

const dropdownOpen = ref(false)
const selectedMacro = ref(null)
const showModal = ref(false)

const dropdownRef = ref(null)

onMounted(async () => {
  try {
    const res = await getMacros({ model_type: props.modelType, is_active: true, page_size: 50 })
    macros.value = res.results ?? res ?? []
  } catch {
    macros.value = []
  } finally {
    loading.value = false
  }
  document.addEventListener('click', onOutsideClick, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onOutsideClick, true)
})

function onOutsideClick(e) {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
    dropdownOpen.value = false
  }
}

function openMacro(macro) {
  selectedMacro.value = macro
  dropdownOpen.value = false
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  selectedMacro.value = null
}
</script>

<template>
  <!-- Only renders when there are macros available for this model type -->
  <div v-if="!loading && macros.length > 0" ref="dropdownRef" class="relative">

    <button
      class="btn ghost small flex items-center gap-1.5"
      @click="dropdownOpen = !dropdownOpen"
    >
      <MdiPlayBoxOutline class="w-4 h-4" />
      {{ $t('ui.run') }}
      <MdiChevronDown
        class="w-3.5 h-3.5 transition-transform"
        :class="{ 'rotate-180': dropdownOpen }"
      />
    </button>

    <div
      v-if="dropdownOpen"
      class="absolute right-0 top-[calc(100%+4px)] z-50 min-w-[200px] bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-xl overflow-hidden"
    >
      <div class="py-1">
        <button
          v-for="macro in macros"
          :key="macro.id"
          type="button"
          class="w-full text-left px-3.5 py-2 text-sm text-[var(--color-text)] hover:bg-white/5 flex items-center gap-2 transition-colors"
          @click="openMacro(macro)"
        >
          <MdiPlayBoxOutline class="w-4 h-4 text-[var(--color-primary)] flex-shrink-0" />
          <span class="truncate">{{ macro.name }}</span>
        </button>
      </div>
    </div>

    <RunMacroModal
      :show="showModal"
      :macro="selectedMacro"
      :prefill-by-type="{ [modelType]: modelId }"
      @close="closeModal"
    />
  </div>
</template>
